import { AfterViewInit, Component, ViewChild, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { merge, of as observableOf } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { SEARCH_FILTERS } from '../../../../../shared/services/local-info/local-info.models';
import { LocalInfoService } from '../../../../../shared/services/local-info/local-info.service';
import { UsersListItemViewModel } from '../../../../pages/users/users/users.models';
import { UsersService } from '../../../../pages/users/users/users.service';
import { MatPaginatorIntlIta } from '../../../services/classes/matPaginatorIntlIta';

@Component({
    selector: 'user-selector',
    templateUrl: './selector.component.html',
    styleUrls: ["./selector.component.scss"],
    providers: [
        {
            provide: MatPaginatorIntl,
            useClass: MatPaginatorIntlIta
        }
    ],
    standalone: false
})

export class UserSelectorDialogComponent implements OnInit, AfterViewInit
{
	// variables
	_searchFilterName = SEARCH_FILTERS.USERS_USERS_SELECTOR;

	// table
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;
	@ViewChild('listModal') listModal;
	_displayedColumns: string[] = ['status', 'fullName', 'email', 'rolesCount', 'actions'];
	_tableData: UsersListItemViewModel[] = [];
	_resultsLength = 0;
	_isLoadingResults = true;

	// search form
	_searchForm: UntypedFormGroup = this.fb.group(
		{
			name: [null],
			email: [null]
		}
	);

	// constructor
	constructor
		(
			private dialogRef: MatDialogRef<UserSelectorDialogComponent>,
			private usersService: UsersService,
			private localInfoService: LocalInfoService,
			private fb: UntypedFormBuilder
		)
	{

	}

	// on init
	ngOnInit()
	{
		// load search filter
		this.loadFilters();
	}

	// after view init
	ngAfterViewInit()
	{
		// get list
		this.getList();
	}

	// load filters
	loadFilters()
	{
		const _listFilter = this.localInfoService.getFilter(this._searchFilterName);
		if (_listFilter)
			this._searchForm.patchValue(_listFilter);
	}

	// reset filter
	resetFilter()
	{
		// remove search filter
		this.localInfoService.removeFilter(this._searchFilterName);

		// reset form
		this._searchForm.reset();

		// get list
		this.getList();
	}

	// search user
	search()
	{
		// save search filter
		this.localInfoService.saveFilter(this._searchFilterName, JSON.stringify(this._searchForm.value));

		// get list
		this.getList();
	}

	// get users list
	getList() 
	{
		merge(this.sort.sortChange, this.paginator.page)
			.pipe(
				startWith({}),
				switchMap(() =>
				{
					// loading
					this._isLoadingResults = true;

					// datatable params
					const datatableParams =
					{
						sort: this.sort.active,
						order: this.sort.direction,
						page: this.paginator.pageIndex,
						pageSize: this.paginator.pageSize
					};

					// filter params
					const _filterParams = { ...datatableParams, ...this._searchForm.value };

					// getting data from service
					return this.usersService.getFiltered(_filterParams);
				}),
				map(data =>
				{
					// loading
					this._isLoadingResults = false;

					// total count
					this._resultsLength = data.totalCount;

					// result data
					return data.items;
				}),
				catchError(() =>
				{
					this._isLoadingResults = false;
					return observableOf([]);
				})
			)
			.subscribe(data => this._tableData = data);
	}

	// select user
	selectUser(user: UsersListItemViewModel)
	{
		this.dialogRef.close(
			{
				id: user.id,
				name: user.name,
				email: user.email,
				status: user.status,
			}
		);
	}
}